/** ⌘K command palette — v1 parity (command-palette.js): one box that jumps
 * anywhere. Searches pages, leads (address/seller), deals, contacts; Enter
 * opens the first hit, click opens any. Esc closes. */
import { useEffect, useMemo, useRef, useState } from "react";
import type { Lead } from "../../types";
import type { Deal } from "../deals/api";
import type { Contact } from "../contacts/api";
import type { TaskRec } from "../edits/api";
import type { WholesaleDeal } from "../wholesale/api";

export interface PaletteHit {
  kind: "page" | "lead" | "deal" | "contact" | "task" | "wholesale";
  label: string;
  sub: string;
  go: () => void;
}

const PAGES: { key: string; label: string }[] = [
  { key: "dashboard", label: "Dashboard" },
  { key: "properties", label: "Properties" },
  { key: "tasks", label: "Tasks" },
  { key: "inbox", label: "Inbox" },
  { key: "mailbox", label: "Emails" },
  { key: "hot", label: "🔥 Hot replies" },
  { key: "deals", label: "Deals" },
  { key: "wholesale", label: "Wholesale" },
  { key: "roi", label: "Net ROI Calculator" },
  { key: "contacts", label: "Contacts" },
  { key: "calendar", label: "Calendar" },
  { key: "team", label: "Team" },
  { key: "analytics", label: "Analytics" },
  { key: "intel", label: "Market Intel" },
  { key: "agent", label: "Text Agent" },
  { key: "materials", label: "Materials" },
  { key: "bizfiles", label: "Business Files" },
];

export function buildHits(
  q: string,
  leads: Lead[],
  deals: Deal[],
  contacts: Contact[],
  tasks: TaskRec[],
  wholesale: WholesaleDeal[],
  nav: (page: string) => void,
  openLead: (address: string) => void,
): PaletteHit[] {
  const needle = q.trim().toLowerCase();
  const out: PaletteHit[] = [];
  for (const p of PAGES) {
    if (!needle || p.label.toLowerCase().includes(needle))
      out.push({ kind: "page", label: p.label, sub: "Go to page", go: () => nav(p.key) });
  }
  if (needle) {
    for (const l of leads) {
      if (out.length >= 12) break;
      if (l.address.toLowerCase().includes(needle) || (l.seller ?? "").toLowerCase().includes(needle))
        out.push({ kind: "lead", label: l.address, sub: `Lead · ${l.source}`, go: () => openLead(l.address) });
    }
    for (const d of deals) {
      if (out.length >= 15) break;
      if (d.address.toLowerCase().includes(needle))
        out.push({ kind: "deal", label: d.address, sub: "Deal", go: () => nav("deals") });
    }
    for (const w of wholesale) {
      if (out.length >= 18) break;
      if (w.address.toLowerCase().includes(needle) || (w.seller ?? "").toLowerCase().includes(needle))
        out.push({ kind: "wholesale", label: w.address, sub: `Wholesale${w.stage ? " · " + w.stage : ""}`, go: () => nav("wholesale") });
    }
    for (const t of tasks) {
      if (out.length >= 22) break;
      if (t.title.toLowerCase().includes(needle))
        out.push({ kind: "task", label: t.title, sub: `Task${t.deal ? " · " + t.deal : ""}`, go: () => nav("tasks") });
    }
    for (const c of contacts) {
      if (out.length >= 26) break;
      if (c.name.toLowerCase().includes(needle) || (c.company ?? "").toLowerCase().includes(needle))
        out.push({ kind: "contact", label: c.name, sub: `Contact${c.type ? " · " + c.type : ""}`, go: () => nav("contacts") });
    }
  }
  return out.slice(0, 26);
}

export function Palette({
  open,
  onClose,
  leads,
  deals,
  contacts,
  tasks,
  wholesale,
  nav,
  openLead,
}: {
  open: boolean;
  onClose: () => void;
  leads: Lead[];
  deals: Deal[];
  contacts: Contact[];
  tasks: TaskRec[];
  wholesale: WholesaleDeal[];
  nav: (page: string) => void;
  openLead: (address: string) => void;
}) {
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const hits = useMemo(
    () => (open ? buildHits(q, leads, deals, contacts, tasks, wholesale, (p) => { nav(p); onClose(); }, (a) => { openLead(a); onClose(); }) : []),
    [open, q, leads, deals, contacts, tasks, wholesale, nav, openLead, onClose],
  );

  if (!open) return null;
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="palette">
        <input
          ref={inputRef}
          placeholder="Jump to a page, lead, deal, wholesale, task, or contact…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && hits[0]) hits[0].go();
            if (e.key === "Escape") onClose();
          }}
        />
        <div className="palhits">
          {hits.map((h, i) => (
            <button key={h.kind + h.label + i} className="palhit" onClick={h.go}>
              <span className="pallabel">{h.label}</span>
              <span className="sub">{h.sub}</span>
            </button>
          ))}
          {q && !hits.length ? <div className="sub" style={{ padding: 10 }}>No matches.</div> : null}
        </div>
      </div>
    </>
  );
}
